package ai.wagemix.web.screens.sharedlinks

import ai.wagemix.logic.sharedlinks.WMDefaultSharedLinksPresenter
import ai.wagemix.logic.sharedlinks.WMSharedLinkRow
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Action
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Event
import ai.wagemix.logic.sharedlinks.WMSharedLinksPresenter.Prompt
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder

interface WMSharedLinksWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    /** The user's shared links, newest first; `null` until the first list arrives. */
    val rowsState: State<List<WMSharedLinkRow>?>
    /** The subscription ended: the page shows the renewal state instead of the links (paid-feature plan W7). */
    val lapsedState: State<Boolean>
}

abstract class WMBaseSharedLinksWebViewModel<P: Prompt, E: Event<P>, A: Action, PR: WMSharedLinksPresenter<P, E, A>>(
    presenter: PR
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMSharedLinksWebViewStateHolder<P> {

    override val rowsState: State<List<WMSharedLinkRow>?> get() = _rowsState
    protected val _rowsState = mutableStateOf<List<WMSharedLinkRow>?>(null)

    override val lapsedState: State<Boolean> get() = _lapsedState
    protected val _lapsedState = mutableStateOf(false)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            rowsUpdate?.let { _rowsState.value = it }
            lapsedUpdate?.let { _lapsedState.value = it }
        }
    }

    fun userRequestedRegeneratePreview(sharedMixId: String) = presenter.userRequestedRegeneratePreview(sharedMixId)
    fun userChangedShareActive(sharedMixId: String, active: Boolean) {
        presenter.userChangedShareActive(sharedMixId, active)
    }
}

typealias WMDefaultSharedLinksWebViewModel = WMBaseSharedLinksWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultSharedLinksPresenter>

fun createDefaultSharedLinksWebViewModel(presenter: WMDefaultSharedLinksPresenter): WMDefaultSharedLinksWebViewModel {
    return object: WMDefaultSharedLinksWebViewModel(presenter) {
        override val defaultPrompt: Prompt.Base
            get() = Prompt.baseFactory().none()
    }
}
